/*!
 * ===========================================================================
 * smart-assistant.js  (Smart Assistant : AI-Powered Search Assistant)
 * ===========================================================================
 * Version : 2.0  (2026-07 : streaming adoption + security hardening)
 *
 * The API-client module behind sections/smart-assistant.liquid. The section's
 * inline script owns ALL DOM rendering ; this module owns the API contract :
 * request building, session persistence, response processing, error mapping,
 * (new in 2.0) consumption of the streaming endpoint, and (2026-09-18) the
 * open-by-link address format (OPEN_LINK_FRAGMENT + parseOpenLink), kept here
 * so the section and the test suite read one definition of it.
 *
 * WHAT CHANGED IN 2.0 (spec : smart-assistant-01 repo,
 * docs/STOREFRONT-STREAMING-HANDOFF.md, 2026-07-06 ; contract reference :
 * that repo's README "API Endpoints" section + docs/ERROR_CODES.md) :
 *
 *  - searchStream() : consumes POST {apiEndpoint}/stream (Server-Sent Events)
 *    so the customer sees the answer being written out live. Browsers cannot
 *    use EventSource for POST, so the stream is read via fetch +
 *    response.body.getReader() + TextDecoder.
 *  - Client-side timeout : every request (JSON and streaming) now carries an
 *    AbortController that fires at REQUEST_TIMEOUT_MS. Before 2.0 a hung
 *    request left the section spinning forever with the inputs disabled.
 *  - Transport fallback : a streaming request that fails at the transport
 *    level BEFORE any event was handled retries exactly once via the JSON
 *    endpoint, so customers behind stream-hostile proxies still get answers.
 *  - XSS fix (2026-07 backend review, highest priority) : formatAnswer() now
 *    HTML-escapes the raw answer text FIRST and only then applies its own
 *    markdown-to-HTML transforms, so markup echoed by the model or sourced
 *    from indexed content renders inert while the feature's own generated
 *    tags survive. Every consumer (JSON path, streaming accumulator, history)
 *    flows through this one function, so the fix lands at the shared root.
 *  - Source URL validation (same review) : processSources() drops any source
 *    whose URL does not resolve to http:/https:, so a javascript: URL can
 *    never become a clickable script link.
 *  - Console hygiene (same review) : the init() config dump is gone ; only
 *    console.error/console.warn for genuine failures/misconfiguration remain.
 *
 * MODULE FORMAT (why the UMD-lite wrapper) : Shopify serves this as a classic
 * <script defer>, but the Jest suite (tests/smart-assistant.test.js) requires
 * it under Node to unit-test the escape pipeline, the SSE parser, the timeout
 * and the fallback semantics. The wrapper (same shape as order-form-v2.js)
 * exports the module surface when module.exports exists and only attaches to
 * window + dispatches the ready event in the browser.
 */
(function (root, factory) {
  // Building the module surface is DOM-free and side-effect-free, so it is
  // safe to run under Jest / Node as well as in the browser.
  var SmartAssistant = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    // CommonJS (Jest / Node) : expose the module for unit tests. No window
    // attachment and no ready event : there is no section to wire.
    module.exports = SmartAssistant;
  } else {
    // Browser. Guard against double-injection (a page rendering the asset
    // twice must keep the first instance rather than clobbering its state).
    if (!root.SmartAssistant) {
      root.SmartAssistant = SmartAssistant;
    }
    // Ready handshake : the section's inline script may run BEFORE this
    // deferred asset (normal parse order) or AFTER it (Shopify theme-editor
    // section re-render). It checks window.SmartAssistant first and falls
    // back to listening for this event, so both orders work without the
    // 100ms setTimeout polling loop this replaced (2026-07 review item :
    // init busy-wait).
    if (root.document && typeof root.CustomEvent === 'function') {
      root.document.dispatchEvent(new root.CustomEvent('smart-assistant:ready'));
    }
  }
})(typeof window !== 'undefined' ? window : this, function (root) {
  'use strict';

  // Configuration defaults for the feature switches and numbers. The section's
  // inline script sets every value from its schema settings via init() ; these
  // are the safety net if one of THOSE is ever missing. The endpoint and the
  // error strings have no default here on purpose (section audit batch 2,
  // 2026-09-11, decision D12 : no hidden fallbacks) : init() throws without an
  // endpoint, and an error code with no configured message yields an empty
  // message, which the section's catch block replaces with its Default Error
  // Message setting, so the wording a customer sees is always the merchant's.
  const DEFAULT_CONFIG = {
    showSources: true,
    maxSources: 3,
    showRelatedQuestions: true,
    enableConversation: true,
    sessionTimeoutMinutes: 30
  };

  // localStorage keys for the conversation session (shared across page loads
  // so a follow-up question keeps its context within the timeout window).
  // HISTORY (2026-07-07, transcript persistence) : the visible transcript is
  // stored NEXT TO the session it belongs to and shares its lifecycle : both
  // are only written around a completed exchange and both are removed
  // together in clearSession(), so a transcript can never outlive its
  // session or be restored into a newer, unrelated conversation.
  const STORAGE_KEYS = {
    SESSION_ID: 'smartAssistantSession',
    SESSION_TIMESTAMP: 'smartAssistantSessionTimestamp',
    HISTORY: 'smartAssistantHistory'
  };

  // The open-by-link address (2026-09-18). A link whose fragment is exactly
  // this marker, optionally followed by ?q=<question>, opens the section
  // (see parseOpenLink below and the section's openAssistant block). The
  // section's wrapper carries the same value as its id, so a plain anchor
  // still scrolls to a visible placement with no script at all : keep the id
  // in sections/smart-assistant.liquid and this constant identical (pinned by
  // tests/smart-assistant-contract.test.js).
  const OPEN_LINK_FRAGMENT = 'ask-trustico-assistant';

  // Transcript caps (2026-07-07, transcript persistence). WHY : localStorage
  // quotas are shared across the whole origin and measured in UTF-16 code
  // units, which is exactly what String.length counts : hence length as the
  // size proxy. An unbounded transcript would grow forever and eventually
  // crowd the origin's shared quota ; 20 exchanges is far beyond a real
  // support conversation, and the 100K-unit payload ceiling keeps the worst
  // case around 2% of the typical 5M-unit quota. Exported on the module
  // surface for the test suite.
  const HISTORY_MAX_ITEMS = 20;
  const HISTORY_MAX_LENGTH = 100 * 1024;

  // HTTP status -> error code mapping for responses whose JSON envelope did
  // not carry an error.code. Codes and statuses per the backend contract
  // (smart-assistant-01 docs/ERROR_CODES.md).
  const STATUS_CODE_MAP = {
    400: 'INVALID_REQUEST',
    401: 'AUTHENTICATION_FAILED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    408: 'REQUEST_TIMEOUT',
    415: 'UNSUPPORTED_MEDIA_TYPE',
    429: 'RATE_LIMIT_EXCEEDED',
    500: 'SEARCH_FAILED',
    503: 'SERVICE_UNAVAILABLE'
  };

  // Client-side abort deadline for BOTH the JSON and the streaming request.
  // The server hard-caps its own work at 30 seconds (smart-assistant-01
  // README, API_TIMEOUT_MS default) ; the 5-second margin means the server's
  // own REQUEST_TIMEOUT normally arrives first and this client abort only
  // fires when the connection itself is hung (the pre-2.0 spin-forever
  // defect). Exported on the module surface for the test suite.
  const REQUEST_TIMEOUT_MS = 35000;

  // Module state : the merged configuration and the init gate. errorMessages
  // starts as an empty map so that, before init() has run, getErrorMessage
  // and makeError yield the documented empty string rather than throwing on
  // an undefined map (DEFAULT_CONFIG carries no error strings : decision D12).
  let config = { ...DEFAULT_CONFIG, errorMessages: {} };
  let isInitialized = false;

  /**
   * SmartAssistant module surface.
   */
  const SmartAssistant = {
    // Exposed for tests/smart-assistant.test.js : pins the 30s-server +5s
    // margin relationship so a future edit that races the server is caught.
    REQUEST_TIMEOUT_MS: REQUEST_TIMEOUT_MS,
    // Exposed for the test suite : the transcript-cap tests assert against
    // the real values instead of duplicating literals.
    HISTORY_MAX_ITEMS: HISTORY_MAX_ITEMS,
    HISTORY_MAX_LENGTH: HISTORY_MAX_LENGTH,
    // Exposed for the section (its wrapper id must match) and the test suite.
    OPEN_LINK_FRAGMENT: OPEN_LINK_FRAGMENT,

    /**
     * Initialize the Smart Assistant module.
     * @param {Object} userConfig - Configuration options from the section's
     *   schema settings (see sections/smart-assistant.liquid init call).
     */
    init(userConfig = {}) {
      // Merge user config with the switch/number defaults. errorMessages is
      // copied as given (there are no defaults to merge with : decision D12)
      // and is always an object so getErrorMessage can index it.
      config = {
        ...DEFAULT_CONFIG,
        ...userConfig,
        errorMessages: { ...(userConfig.errorMessages || {}) }
      };

      // Validate required configuration.
      if (!config.apiEndpoint) {
        throw new Error('SMART ASSISTANT : apiEndpoint IS REQUIRED');
      }

      // Remove a trailing slash so the /stream suffix derivation in
      // searchStream() can never produce a double slash.
      config.apiEndpoint = config.apiEndpoint.replace(/\/$/, '');

      // Guard maxSources : aligned to the schema's 1-5 range (PR #58 round-3
      // review, observation 1 : the pre-2.0 guard said 1-10 while the schema
      // caps at 5, leaving three conflicting numbers between guard, message
      // and comment). The theme editor can never produce an out-of-range
      // value, so this only fires on a programmatic init : still worth the
      // loud warning because a silent clamp would mask the misconfiguration.
      if (config.maxSources < 1 || config.maxSources > 5) {
        console.warn('SMART ASSISTANT : maxSources MUST BE BETWEEN 1 AND 5 : USING DEFAULT 3');
        config.maxSources = 3;
      }

      isInitialized = true;
      // NOTE : no config dump here. The pre-2.0 init logged the full
      // configuration (endpoint + all strings) to the console on every page
      // load (2026-07 backend review, console-noise item).
    },

    /**
     * Get the current session ID if valid and not expired.
     * @returns {string|null} Session ID or null if invalid/expired.
     */
    getSession() {
      if (!config.enableConversation) {
        return null;
      }

      try {
        const sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
        const timestamp = localStorage.getItem(STORAGE_KEYS.SESSION_TIMESTAMP);

        if (!sessionId || !timestamp) {
          return null;
        }

        // Expire the session client-side after the merchant-configured
        // timeout : the backend transparently restarts stale sessions anyway
        // (README "Stale Sessions"), but expiring here keeps the customer's
        // expectation of a "fresh" conversation aligned with the setting.
        const sessionAge = Date.now() - parseInt(timestamp, 10);
        const sessionTimeout = config.sessionTimeoutMinutes * 60 * 1000;

        if (sessionAge > sessionTimeout) {
          this.clearSession();
          return null;
        }

        return sessionId;
      } catch (error) {
        // localStorage can throw (private browsing, storage disabled) : a
        // failed read just means no conversation context, never a hard error.
        console.error('SMART ASSISTANT : ERROR GETTING SESSION :', error);
        return null;
      }
    },

    /**
     * Save the session ID with a freshness timestamp. Fed by the JSON path's
     * data.sessionId AND the streaming path's `session` event : both store
     * through this one method so the conversation flow is path-independent.
     * @param {string} sessionId - Session ID to save.
     */
    saveSession(sessionId) {
      if (!config.enableConversation) {
        return;
      }

      try {
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
        localStorage.setItem(STORAGE_KEYS.SESSION_TIMESTAMP, Date.now().toString());
      } catch (error) {
        console.error('SMART ASSISTANT : ERROR SAVING SESSION :', error);
      }
    },

    /**
     * Clear the current session (Start Fresh button, or expiry). Also clears
     * the persisted transcript (2026-07-07) : session and transcript share
     * one lifecycle, and wiring the removal HERE means every existing clear
     * site : expiry inside getSession(), the section's Reset Conversation
     * handler : covers the transcript automatically, with no new call sites
     * to forget.
     */
    clearSession() {
      try {
        localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
        localStorage.removeItem(STORAGE_KEYS.SESSION_TIMESTAMP);
        localStorage.removeItem(STORAGE_KEYS.HISTORY);
      } catch (error) {
        console.error('SMART ASSISTANT : ERROR CLEARING SESSION :', error);
      }
    },

    /**
     * Persist the visible transcript (2026-07-07, transcript persistence).
     * Called by the section ONLY from its completed-exchange push site and
     * ONLY when that instance's persist_history setting is on : storage is
     * one browser-wide transcript (same scope as the session id), the
     * per-instance checkbox just decides whether a given placement reads and
     * writes it. A chat held on an enabled page therefore reappears on other
     * enabled pages, while pages with the option off are simply blind to it.
     *
     * Only the RAW TEXT fields are stored : never the pre-rendered
     * answerHtml. WHY : persisting HTML would make localStorage a direct
     * HTML-injection channel into the history accordion ; getHistory()
     * regenerates the HTML through the escape-first formatAnswer pipeline
     * instead, so storage cannot smuggle markup into the page by
     * construction (and the payload roughly halves).
     * @param {Array} items - The section's conversationHistory array.
     */
    saveHistory(items) {
      if (!config.enableConversation || !Array.isArray(items)) {
        return;
      }

      try {
        // Newest HISTORY_MAX_ITEMS survive ; oldest trim first (the array is
        // push-ordered, oldest first). String() here NORMALIZES a trusted
        // in-memory value, while getHistory() strictly REJECTS non-strings :
        // deliberately different strictness for different trust boundaries
        // (PR #61 round-2 review, observation 2).
        let slim = items.slice(-HISTORY_MAX_ITEMS).map(function (item) {
          const entry = {
            question: String(item.question),
            answer: String(item.answer)
          };
          // sessionId is informational (feeds the history Continue button on
          // restore) ; absent stays absent so JSON stays minimal.
          if (typeof item.sessionId === 'string') {
            entry.sessionId = item.sessionId;
          }
          return entry;
        });

        // Payload ceiling : trim oldest-first until under the cap. A single
        // pathological exchange larger than the whole cap ends with an empty
        // list, which is the correct degradation (persist nothing rather
        // than throw or overflow the quota).
        let serialized = JSON.stringify(slim);
        while (serialized.length > HISTORY_MAX_LENGTH && slim.length > 0) {
          slim = slim.slice(1);
          serialized = JSON.stringify(slim);
        }

        if (slim.length === 0) {
          localStorage.removeItem(STORAGE_KEYS.HISTORY);
          return;
        }
        localStorage.setItem(STORAGE_KEYS.HISTORY, serialized);
      } catch (error) {
        // Same degradation contract as the session functions : storage
        // failures (private browsing, quota) silently disable persistence ;
        // the in-page conversation keeps working.
        console.error('SMART ASSISTANT : ERROR SAVING HISTORY :', error);
      }
    },

    /**
     * Load the persisted transcript for restore (2026-07-07, transcript
     * persistence). Returns [] unless conversation is enabled AND the
     * existing session-validity check passes : getSession() owns the
     * timeout arithmetic (reused, never duplicated) and already clears an
     * expired session, which removes the transcript with it via
     * clearSession().
     *
     * The stored value is UNTRUSTED input : parsed defensively (corruption
     * removes the store and starts clean, never throws) and shape-validated
     * strictly : an array of objects whose question/answer are strings
     * (sessionId optional string) ; anything malformed is dropped. Each
     * surviving item gets its answerHtml REGENERATED through the
     * escape-first formatAnswer pipeline : HTML is never read from storage,
     * so crafted markup in a tampered store renders inert as text.
     * @returns {Array} History items in the section's in-memory shape.
     */
    getHistory() {
      if (!config.enableConversation) {
        return [];
      }

      // Session gone or expired : any lingering transcript is stale by
      // definition. getSession()'s expiry path already ran clearSession()
      // (which removes the transcript) ; the explicit clearHistory() covers
      // the id-and-timestamp-missing case where no expiry fired.
      if (!this.getSession()) {
        this.clearHistory();
        return [];
      }

      let raw = null;
      try {
        raw = localStorage.getItem(STORAGE_KEYS.HISTORY);
      } catch (error) {
        console.error('SMART ASSISTANT : ERROR READING HISTORY :', error);
        return [];
      }
      if (!raw) {
        return [];
      }

      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (parseError) {
        // Corrupted store : remove it and start clean, never throw.
        console.error('SMART ASSISTANT : CORRUPTED STORED HISTORY DISCARDED :', parseError);
        this.clearHistory();
        return [];
      }

      if (!Array.isArray(parsed)) {
        // Valid JSON of the wrong overall shape is corruption too.
        this.clearHistory();
        return [];
      }

      const self = this;
      return parsed
        .filter(function (entry) {
          return entry !== null &&
            typeof entry === 'object' &&
            typeof entry.question === 'string' &&
            typeof entry.answer === 'string' &&
            (entry.sessionId === undefined || typeof entry.sessionId === 'string');
        })
        // Symmetric read-side cap (PR #61 round-1 review) : saveHistory
        // enforces the item cap on write, but a hand-stuffed store could
        // otherwise restore unbounded entries. Newest survive, matching the
        // write side ; sliced BEFORE the map so dropped entries never pay
        // for HTML regeneration.
        .slice(-HISTORY_MAX_ITEMS)
        .map(function (entry) {
          return {
            question: entry.question,
            answer: entry.answer,
            // Regenerated, never restored : see the method comment.
            answerHtml: self.formatAnswer(entry.answer),
            sessionId: entry.sessionId
          };
        });
    },

    /**
     * Remove the persisted transcript alone (stale-store cleanup inside
     * getHistory()). Full clears go through clearSession(), which removes
     * the transcript alongside the session.
     */
    clearHistory() {
      try {
        localStorage.removeItem(STORAGE_KEYS.HISTORY);
      } catch (error) {
        console.error('SMART ASSISTANT : ERROR CLEARING HISTORY :', error);
      }
    },

    /**
     * Strip our own storefront domain from source URLs so on-site sources
     * navigate as relative links (no full page-context loss on http->https
     * or www variants). Off-site URLs pass through untouched and are then
     * subject to the scheme check in processSources().
     *
     * KNOWN LIMITATION (PR #59 round-2 review, observation 1, documented by
     * choice) : the canonical host is hardcoded rather than derived from
     * location.origin, so when the theme is viewed on a PREVIEW host
     * (*.myshopify.com / *.shopifypreview.com) a stripped on-site source
     * resolves against the preview origin instead of the live store. That
     * only affects staff previews, never customers, and the scheme check
     * still applies either way ; deriving the host from location would flip
     * the trade-off (preview links would leave the preview theme for the
     * live site) without helping customers, so the simple hardcode stays.
     * @param {string} url - Full URL to transform.
     * @returns {string} Relative URL for on-site sources, original otherwise.
     */
    stripDomain(url) {
      if (!url) return url;
      // Domain-anchored (PR #59 review, observation 2) : the boundary
      // lookahead requires the host to END at shop.trustico.com, so a
      // lookalike host (shop.trustico.com.evil.com) no longer matches the
      // prefix and produces a silently-wrong on-origin link : such URLs now
      // pass through absolute and render as exactly what they are (the
      // http/https scheme check in processSources still applies to them).
      const stripped = url.replace(/^https?:\/\/shop\.trustico\.com(?=\/|$)/, '');
      // The bare homepage strips to an empty string, which the falsy guard
      // in isSafeSourceUrl would then DISCARD : an on-origin source must
      // render (as the homepage link), not silently vanish (PR #59 round-3
      // review, observation 1). Only a whole-URL match can produce '' here,
      // because empty input already returned above.
      return stripped === '' ? '/' : stripped;
    },

    /**
     * HTML-escape the five characters that can change parsing context. This
     * is the FIRST step of formatAnswer()'s escape-then-format pipeline (the
     * 2026-07 XSS fix) : after this, nothing in the source text can open a
     * tag, close an attribute, or start an entity of its own.
     * @param {string} text - Raw untrusted text.
     * @returns {string} Entity-escaped text, safe to embed in HTML.
     */
    escapeHtml(text) {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    /**
     * Format answer text : ESCAPE FIRST, then convert the answer's own
     * markdown-ish tokens to HTML.
     *
     * WHY this order (2026-07 backend review, XSS item) : the transforms
     * below only rewrite markdown tokens : they never neutralized raw HTML,
     * so markup echoed by the model or lifted from indexed page content
     * became live DOM at the innerHTML sinks. Escaping first renders any
     * injected markup inert as visible text, while the <h2>/<p>/<strong>/<em>
     * tags GENERATED below (after escaping) survive untouched. The escaped
     * entities (&amp; etc.) contain none of the characters the markdown
     * regexes match on, so the transforms behave identically on clean text.
     *
     * Every render path uses this one function : the JSON result, the
     * streaming delta accumulator, and the history re-render : so the escape
     * cannot be skipped by one path drifting.
     * @param {string} text - Raw answer text from the API.
     * @returns {string} Safe HTML for the answer containers.
     */
    formatAnswer(text) {
      if (!text) return '';

      // Step 0 (2.0) : the escape-first XSS fix. Everything below operates on
      // entity-escaped text.
      let html = this.escapeHtml(text);

      // Step 1 : Normalize line endings.
      html = html.replace(/\r\n/g, '\n');

      // Step 2 : Convert main bullet points with bold labels to h2 headings.
      // Pattern : *   **Label**: or *   **Label**:\n
      html = html.replace(/^\*\s{1,3}\*\*([^*]+)\*\*:\s*/gm, '\n<h2>$1</h2>\n');

      // Step 3 : Convert nested bullet points to paragraphs.
      // Pattern :     *   **Label** content or     *   content
      html = html.replace(/^\s{2,4}\*\s{1,3}\*\*([^*]+)\*\*\s*/gm, '<p><strong>$1</strong> ');
      html = html.replace(/^\s{2,4}\*\s{1,3}/gm, '<p>');

      // Step 4 : Convert remaining bullet points at start of line to paragraphs.
      html = html.replace(/^\*\s{1,3}/gm, '<p>');

      // Step 5 : Convert remaining **bold** to <strong>.
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

      // Step 6 : Convert *italic* to <em> (but not bullet points). The
      // lookbehind requires Safari 16.4+ (2023) to PARSE : older engines
      // SyntaxError at load. Within the theme's 2026 browser baseline
      // (PR #58 round-3 review, observation 3 : awareness note only).
      html = html.replace(/(?<!\n)\*([^*\n]+)\*/g, '<em>$1</em>');

      // Step 7 : Split into paragraphs by double newlines.
      const blocks = html.split(/\n\n+/);

      // Step 8 : Process each block.
      const processedBlocks = blocks.map(block => {
        block = block.trim();
        if (!block) return '';

        // Already has generated HTML tags (h2 or starts with <p> or <strong>).
        if (block.startsWith('<h2>') || block.startsWith('<p>') || block.startsWith('<strong>')) {
          // If it starts with <p> or <strong> but doesn't end with </p>, close it.
          if ((block.startsWith('<p>') || block.startsWith('<strong>')) && !block.endsWith('</p>')) {
            // Handle blocks that start with <p><strong> - need to close properly.
            if (!block.includes('</p>')) {
              block = block + '</p>';
            }
          }
          return block;
        }

        // Wrap plain text in paragraph tags.
        return '<p>' + block.replace(/\n/g, ' ') + '</p>';
      });

      // Step 9 : Join and clean up.
      html = processedBlocks.filter(b => b).join('\n');

      // Step 10 : Clean up any double paragraph tags or empty paragraphs.
      html = html.replace(/<p>\s*<\/p>/g, '');
      html = html.replace(/<p>\s*<p>/g, '<p>');
      html = html.replace(/<\/p>\s*<\/p>/g, '</p>');

      // Step 11 : Ensure paragraphs are properly closed.
      // Find <p> tags that don't have a closing </p> before the next <p> or <h2>.
      html = html.replace(/<p>([^<]*?)(?=<p>|<h2>|$)/g, '<p>$1</p>');

      return html;
    },

    /**
     * Whether a source URL is safe to render as a link : it must resolve to
     * http: or https: (relative URLs resolve against the storefront origin,
     * so they pass). Anything else : javascript:, data:, vbscript:, an
     * unparseable string : is rejected, because renderSources assigns the
     * value to an anchor href where a script scheme would execute on click
     * (2026-07 backend review, unvalidated-source-URL item).
     * @param {string} url - Candidate source URL (post-stripDomain).
     * @returns {boolean} True when the URL is http(s) or storefront-relative.
     */
    isSafeSourceUrl(url) {
      if (!url || typeof url !== 'string') return false;
      try {
        const parsed = new URL(url, root.location && root.location.origin ? root.location.origin : 'https://shop.trustico.com');
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch (error) {
        // An unparseable URL cannot be safely linked : reject it.
        return false;
      }
    },

    /**
     * Process a raw sources array from the API into the display list : the
     * show-sources gate, domain stripping, the URL scheme check, and the
     * max-sources cap : in that order, so an unsafe entry never consumes a
     * display slot. Shared by BOTH paths : the JSON response's data.sources
     * and every streaming `sources` event flow through here, keeping the
     * section's renderSources a pure renderer with a single guard site.
     * @param {Array} sources - Raw sources ([{title,url,snippet}]) from the API.
     * @returns {Array} Filtered, stripped, capped sources for rendering.
     */
    processSources(sources) {
      if (!config.showSources || !Array.isArray(sources) || sources.length === 0) {
        return [];
      }

      const safe = [];
      for (const source of sources) {
        const url = this.stripDomain(source && source.url);
        if (!this.isSafeSourceUrl(url)) continue;
        safe.push({ ...source, url: url });
        if (safe.length >= config.maxSources) break;
      }
      return safe;
    },

    /**
     * Gate related questions on the merchant setting. Tiny, but shared by
     * both paths for the same single-site reason as processSources().
     * @param {Array} relatedQuestions - Raw related questions from the API.
     * @returns {Array} The list, or empty when the feature is disabled.
     */
    processRelatedQuestions(relatedQuestions) {
      if (!config.showRelatedQuestions || !Array.isArray(relatedQuestions)) {
        return [];
      }
      return relatedQuestions;
    },

    /**
     * Read an open-by-link address (2026-09-18). Takes a URL fragment with or
     * without its leading '#' : `#ask-trustico-assistant` opens the section,
     * `#ask-trustico-assistant?q=How+do+I+generate+a+CSR` opens it and asks
     * that question. The question is read with URLSearchParams so `+` and
     * `%20` spacing both work, whichever the link's author typed, and a blank
     * question opens without one. Anything else (another fragment, a near
     * miss such as `#ask-trustico-assistant-x`, no hash at all) is not an
     * open link. Length is not policed here : validateQuery applies the
     * 500-character rule when the question is asked, exactly as for typed
     * input, so an over-long link shows the same message a customer would.
     * @param {string} hash - location.hash, or an anchor element's hash.
     * @returns {{question: (string|null)}|null} The question to ask (null to
     *   open without one), or null when the fragment is not an open link.
     */
    parseOpenLink(hash) {
      if (typeof hash !== 'string') return null;
      const fragment = hash.charAt(0) === '#' ? hash.slice(1) : hash;
      if (fragment === OPEN_LINK_FRAGMENT) {
        return { question: null };
      }
      const prefix = OPEN_LINK_FRAGMENT + '?';
      if (!fragment.startsWith(prefix)) {
        return null;
      }
      const question = (new URLSearchParams(fragment.slice(prefix.length)).get('q') || '').trim();
      return { question: question || null };
    },

    /**
     * Get the configured customer-facing message for an error code or HTTP
     * status. Only the messages passed to init() exist : with none configured
     * for the code or for DEFAULT the result is an empty string, never wording
     * of the module's own (decision D12), and the section's catch block then
     * shows its Default Error Message setting.
     * @param {string|number} errorCodeOrStatus - Error code or HTTP status.
     * @returns {string} The configured message, or an empty string.
     */
    getErrorMessage(errorCodeOrStatus) {
      // If it's a number, treat it as an HTTP status code.
      if (typeof errorCodeOrStatus === 'number') {
        const errorCode = STATUS_CODE_MAP[errorCodeOrStatus];
        return config.errorMessages[errorCode] || config.errorMessages.DEFAULT || '';
      }

      // If it's a string, treat it as an error code.
      return config.errorMessages[errorCodeOrStatus] || config.errorMessages.DEFAULT || '';
    },

    /**
     * Build an Error carrying the mapped message plus the machine-readable
     * code (and status when known). One constructor for every failure site so
     * the section's catch block can rely on error.message/error.code shape.
     * @param {string} code - Error code (contract code or client-side code).
     * @param {number} [status] - HTTP status, when the failure had one.
     * @returns {Error} Mapped error, ready to throw.
     */
    makeError(code, status) {
      const error = new Error(this.getErrorMessage(code));
      error.code = code;
      if (status !== undefined) {
        error.status = status;
      }
      return error;
    },

    /**
     * Shared query validation for both search paths. Mirrors the server's
     * INVALID_REQUEST rules (non-empty, max 500 characters : see
     * smart-assistant-01 docs/ERROR_CODES.md INVALID_REQUEST) so obviously
     * bad input fails fast without a round trip.
     * @param {string} query - Raw query text.
     * @returns {string} Trimmed query, guaranteed non-empty and <= 500 chars.
     * @throws {Error} Mapped INVALID_REQUEST error on failure.
     */
    validateQuery(query) {
      if (!isInitialized) {
        throw new Error('SMART ASSISTANT : MODULE NOT INITIALIZED : CALL init() FIRST');
      }

      if (!query || typeof query !== 'string') {
        throw this.makeError('INVALID_REQUEST');
      }

      const trimmedQuery = query.trim();
      if (trimmedQuery.length === 0) {
        throw this.makeError('INVALID_REQUEST');
      }

      if (trimmedQuery.length > 500) {
        // Message text predates 2.0 and is kept byte-identical (customer-
        // facing wording is the operator's domain).
        const error = new Error('Question Too Long. Please Shorten and Try Again');
        error.code = 'INVALID_REQUEST';
        throw error;
      }

      return trimmedQuery;
    },

    /**
     * Build the request body shared by both endpoints : the query plus the
     * conversation session (when one is live). Same body for /stream by
     * contract (README POST /api/search/stream : "same request body").
     * @param {string} trimmedQuery - Output of validateQuery().
     * @returns {Object} JSON-serializable request body.
     */
    buildRequestBody(trimmedQuery) {
      const requestBody = { query: trimmedQuery };
      const sessionId = this.getSession();
      if (sessionId) {
        requestBody.sessionId = sessionId;
      }
      return requestBody;
    },

    /**
     * Assemble the result object both search paths resolve with. Sources and
     * related questions arrive here ALREADY processed (processSources /
     * processRelatedQuestions ran at the response/event site).
     * @param {string} answer - Authoritative raw answer text.
     * @param {Array} sources - Processed sources.
     * @param {Array} relatedQuestions - Processed related questions.
     * @param {string|null} sessionId - Session ID from the response/stream.
     * @returns {Object} {answer, answerHtml, sources, relatedQuestions, sessionId}.
     */
    buildResult(answer, sources, relatedQuestions, sessionId) {
      // Boundary normalization : downstream consumers (history truncation,
      // the accumulator, formatAnswer) assume answer is a string. A missing
      // answer degrades to '' here, which the section renders as its
      // configured no-answer text : the same tolerance the JSON path has
      // always shown for empty answers.
      const safeAnswer = typeof answer === 'string' ? answer : '';
      return {
        answer: safeAnswer,
        answerHtml: this.formatAnswer(safeAnswer),
        sources: sources,
        relatedQuestions: relatedQuestions,
        // Fall back to the stored session so the section's history entries
        // always carry a usable "continue this conversation" target.
        sessionId: sessionId || this.getSession()
      };
    },

    /**
     * Perform a search via the JSON endpoint. This is the pre-2.0 behavior
     * plus the client-side abort timeout ; it remains fully supported as the
     * kill-switch path (enable_streaming off) and as searchStream()'s
     * transport-level fallback, so its contract handling must not drift
     * (handoff "What must NOT change").
     * @param {string} query - User's search query.
     * @returns {Promise<Object>} buildResult() shape.
     */
    async search(query) {
      const trimmedQuery = this.validateQuery(query);
      const requestBody = this.buildRequestBody(trimmedQuery);

      // Abort a hung request after the shared deadline : pre-2.0 there was no
      // timeout at all, which left the section spinning forever (live defect,
      // handoff "Client-side timeout and fallback").
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        let response;
        try {
          response = await root.fetch(config.apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            signal: controller.signal
          });
        } catch (fetchError) {
          // The abort deadline maps to the timeout message, NOT the generic
          // network message : the customer's actionable difference is
          // "try again" vs "check your connection".
          if (fetchError.name === 'AbortError') {
            throw this.makeError('REQUEST_TIMEOUT');
          }
          console.error('SMART ASSISTANT : NETWORK ERROR :', fetchError);
          throw this.makeError('NETWORK_ERROR');
        }

        // Parse the JSON envelope.
        let responseData;
        try {
          responseData = await response.json();
        } catch (parseError) {
          console.error('SMART ASSISTANT : ERROR PARSING RESPONSE :', parseError);
          const error = new Error(this.getErrorMessage('DEFAULT'));
          error.code = 'PARSE_ERROR';
          throw error;
        }

        // Map error responses through the contract : the envelope's
        // error.code wins, the HTTP status is the fallback.
        if (!response.ok || !responseData.success) {
          const errorCode = (responseData.error && responseData.error.code) || STATUS_CODE_MAP[response.status] || 'DEFAULT';
          throw this.makeError(errorCode, response.status);
        }

        // Normalize a malformed success envelope (success:true with no data
        // payload) to an empty object : the string-normalization in
        // buildResult then degrades it to the section's no-answer text
        // instead of a raw TypeError reaching the customer's error display.
        const data = responseData.data || {};

        // sessionId is always a real session name on success (handoff §1.1) ;
        // persist it for the follow-up question.
        if (data.sessionId) {
          this.saveSession(data.sessionId);
        }

        return this.buildResult(
          data.answer,
          this.processSources(data.sources || []),
          this.processRelatedQuestions(data.relatedQuestions || []),
          data.sessionId
        );
      } finally {
        clearTimeout(timeoutId);
      }
    },

    /**
     * Parse one SSE frame into {event, data}. Frames are separated by a blank
     * line ; each has an `event: <name>` line and a `data: <single-line
     * JSON>` line (handoff §1.2 ; verified against the live dev endpoint,
     * LF-only line endings). Returns null for a frame that does not parse :
     * the caller SKIPS such frames rather than killing an otherwise healthy
     * stream, because loss of the terminal event is already handled by the
     * no-terminal path in searchStream(). Exported for direct unit testing.
     * @param {string} frame - One frame's text (no trailing blank line).
     * @returns {{event: string, data: Object}|null} Parsed frame or null.
     */
    parseSseFrame(frame) {
      const eventMatch = frame.match(/^event: (.+)$/m);
      const dataMatch = frame.match(/^data: (.+)$/m);
      if (!eventMatch || !dataMatch) {
        return null;
      }
      try {
        // Strip a trailing CR from the event name (PR #58 round-1 review,
        // finding 2 ; pairs with the CRLF-tolerant frame SPLIT in
        // searchStream's read loop). Inside a CRLF frame the line break is
        // \r\n, and `$` in JS multiline regex matches before `\n` only, so
        // the capture would keep the `\r`, every event name would miss the
        // switch, and the stream would die dark as NETWORK_ERROR with no
        // fallback. The data capture needs no strip : JSON.parse tolerates
        // trailing whitespace.
        return { event: eventMatch[1].replace(/\r$/, ''), data: JSON.parse(dataMatch[1]) };
      } catch (parseError) {
        console.error('SMART ASSISTANT : MALFORMED STREAM FRAME SKIPPED :', parseError);
        return null;
      }
    },

    /**
     * Perform a search via the streaming endpoint ({apiEndpoint}/stream).
     *
     * Event handling per the contract (handoff §1.2 / README POST
     * /api/search/stream) : event ORDER IS NOT GUARANTEED except that exactly
     * one of done/error terminates the stream (live testing shows `session`
     * arriving near the END) ; `answer` deltas append in display order ;
     * `sources` REPLACES on every emission ; `done.answer` is the
     * authoritative full text that REPLACES the accumulation.
     *
     * Failure semantics (handoff §1.2-1.3) :
     *  - Pre-stream failure arrives as a normal JSON envelope with a real
     *    HTTP status (detected via Content-Type) : mapped through the same
     *    error handling as the JSON path. NO fallback : the JSON endpoint
     *    would fail identically.
     *  - Mid-stream failure arrives as the terminal `error` event : mapped
     *    identically. NO fallback.
     *  - The abort deadline shows REQUEST_TIMEOUT. NO fallback (a second
     *    35-second hang would double the customer's wait for nothing).
     *  - ONLY a transport-level failure BEFORE any event was handled
     *    (network error, missing stream body, unexpected non-JSON content
     *    type) falls back : exactly once : to search(), so customers behind
     *    stream-hostile proxies still get answers.
     *
     * @param {string} query - User's search query.
     * @param {Object} [callbacks] - Optional incremental-render hooks, all
     *   invoked with ALREADY-PROCESSED data :
     *   onDelta(deltaText), onSources(sources), onRelated(questions).
     * @returns {Promise<Object>} buildResult() shape (from the authoritative
     *   done event).
     */
    async searchStream(query, callbacks) {
      callbacks = callbacks || {};
      const trimmedQuery = this.validateQuery(query);
      const requestBody = this.buildRequestBody(trimmedQuery);

      // /stream is DERIVED from the one api_endpoint setting (handoff §1.2 :
      // "do NOT add a second endpoint setting") ; init() stripped any
      // trailing slash.
      const streamUrl = config.apiEndpoint + '/stream';

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      // True once ANY frame has parsed : the gate that forbids falling back
      // after content may have rendered (handoff §1.3 : "Do not fall back
      // after events have rendered"). DELIBERATELY includes frames that were
      // received but ignored (unknown event names) : a parsed frame proves
      // the stream endpoint is alive and speaking SSE, so a retry would most
      // likely fail identically or duplicate work (PR #58 round-2 review,
      // observation 1 : conservative choice, accepted).
      let eventsHandled = false;

      try {
        let response;
        try {
          response = await root.fetch(streamUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            signal: controller.signal
          });
        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            throw this.makeError('REQUEST_TIMEOUT');
          }
          // Transport failure before any bytes : the one sanctioned fallback.
          // NOTE (PR #58 round-3 review, observation 2) : the fallback
          // search() arms its own fresh deadline while this call's timer
          // stays live (harmlessly : it targets an already-settled fetch).
          // The budgets only compound when the first leg dies LATE in its
          // window, which the fast-failure fallback triggers make unlikely.
          console.error('SMART ASSISTANT : STREAM TRANSPORT FAILED : FALLING BACK TO JSON :', fetchError);
          return await this.search(query);
        }

        const contentType = (response.headers.get('content-type') || '');

        if (contentType.indexOf('text/event-stream') === -1) {
          if (contentType.indexOf('application/json') !== -1) {
            // Pre-stream failure : normal JSON envelope with a real HTTP
            // status (handoff §1.2 "Failure before the stream starts").
            // Mapped like the JSON path ; deliberately NOT a fallback case.
            let payload = null;
            try {
              payload = await response.json();
            } catch (parseError) {
              console.error('SMART ASSISTANT : ERROR PARSING PRE-STREAM RESPONSE :', parseError);
            }
            const errorCode = (payload && payload.error && payload.error.code) || STATUS_CODE_MAP[response.status] || 'DEFAULT';
            throw this.makeError(errorCode, response.status);
          }
          // Unexpected content type that is not the JSON-error case :
          // transport-level per handoff §1.3 : fall back once.
          console.error('SMART ASSISTANT : UNEXPECTED STREAM CONTENT TYPE : FALLING BACK TO JSON :', contentType);
          return await this.search(query);
        }

        if (!response.body || typeof response.body.getReader !== 'function') {
          // A streaming response without a readable body means the
          // environment cannot stream at all : exactly the stream-hostile
          // case the fallback exists for.
          console.error('SMART ASSISTANT : STREAM BODY NOT READABLE : FALLING BACK TO JSON');
          return await this.search(query);
        }

        // ---- SSE consumption loop (reference loop from the handoff) ----
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Stream-scoped state. `sources`/`related` keep only the LAST
        // emission (replace semantics) ; deltas are forwarded, not stored :
        // done.answer is authoritative so a module-side accumulator would be
        // dead weight.
        let doneEvent = null;
        let errorEvent = null;
        let lastSources = [];
        let lastRelated = [];
        let streamSessionId = null;

        try {
          // One frame's dispatch, shared by the read loop and the tail
          // flush below. Arrow function : keeps `this` for the module
          // methods and closes over the stream-scoped state.
          const processFrame = (frameText) => {
            const parsed = this.parseSseFrame(frameText);
            if (!parsed) return;
            eventsHandled = true;

            switch (parsed.event) {
              case 'session':
                // Store exactly like the JSON path's data.sessionId
                // (handoff event table). May arrive near the END : never
                // assume it comes first.
                if (parsed.data.sessionId) {
                  streamSessionId = parsed.data.sessionId;
                  this.saveSession(streamSessionId);
                }
                break;
              case 'sources':
                // REPLACE, never append : the event may fire more than once.
                lastSources = this.processSources(parsed.data.sources || []);
                if (callbacks.onSources) callbacks.onSources(lastSources);
                break;
              case 'answer':
                if (typeof parsed.data.delta === 'string' && callbacks.onDelta) {
                  callbacks.onDelta(parsed.data.delta);
                }
                break;
              case 'related':
                lastRelated = this.processRelatedQuestions(parsed.data.relatedQuestions || []);
                if (callbacks.onRelated) callbacks.onRelated(lastRelated);
                break;
              case 'done':
                doneEvent = parsed.data;
                break;
              case 'error':
                errorEvent = parsed.data;
                break;
              default:
                // Unknown event names are ignored for forward compatibility
                // (a future backend event must not break deployed themes).
                break;
            }
          };

          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            // Frames are separated by a blank line. The live backend emits
            // LF-only ("\n\n"), but the SSE spec also allows CRLF framing
            // ("\r\n\r\n"), which contains NO two adjacent \n characters :
            // a plain '\n\n' split would never fire and the whole stream
            // would sit unparsed in the buffer until it died dark (PR #58
            // round-1 review, finding 2). The optional-CR pattern accepts
            // both framings ; a chunk boundary landing INSIDE a separator is
            // safe because an incomplete separator does not match, so the
            // tail stays buffered (frames.pop) until the rest arrives.
            const frames = buffer.split(/\r?\n\r?\n/);
            buffer = frames.pop() || '';

            for (const frame of frames) {
              processFrame(frame);
            }

            // The terminal event is always last (contract §1.2 ordering rule
            // 1) : stop reading as soon as one arrives so a connection held
            // open past it cannot run us into the abort deadline.
            if (doneEvent || errorEvent) {
              // Best-effort close ; failure to cancel is inconsequential
              // because we already have the terminal event.
              reader.cancel().catch(function () {});
              break;
            }
          }

          // Tail flush (PR #59 review, observation 1) : a non-compliant or
          // proxy-rewritten stream may close right after the final frame's
          // data line WITHOUT the terminating blank line, leaving one
          // complete frame stranded in the buffer : without this flush a
          // successful answer would be misreported as "ended without
          // terminal event". The no-argument decode() first drains any
          // multibyte remainder the streaming decoder held back.
          if (!doneEvent && !errorEvent) {
            buffer += decoder.decode();
            if (buffer.trim() !== '') {
              processFrame(buffer);
            }
          }
        } catch (readError) {
          if (readError.name === 'AbortError') {
            throw this.makeError('REQUEST_TIMEOUT');
          }
          if (!eventsHandled) {
            // Connection died before anything was handled : still the
            // transport-level fallback case.
            console.error('SMART ASSISTANT : STREAM READ FAILED : FALLING BACK TO JSON :', readError);
            return await this.search(query);
          }
          // Content already rendered : falling back would duplicate work and
          // could show a second, different answer. Surface the failure.
          console.error('SMART ASSISTANT : STREAM READ FAILED MID-RENDER :', readError);
          throw this.makeError('NETWORK_ERROR');
        }

        if (errorEvent) {
          // Terminal failure mid-stream : same codes as the JSON API
          // (handoff event table) : display exactly as a JSON-path error.
          throw this.makeError(errorEvent.code || 'DEFAULT');
        }

        if (!doneEvent || typeof doneEvent.answer !== 'string') {
          // The stream ended without a terminal event : contract violation or
          // silent connection cut. Same split as a read failure : fallback
          // only when nothing was handled.
          if (!eventsHandled) {
            console.error('SMART ASSISTANT : STREAM ENDED WITHOUT TERMINAL EVENT : FALLING BACK TO JSON');
            return await this.search(query);
          }
          console.error('SMART ASSISTANT : STREAM ENDED WITHOUT TERMINAL EVENT');
          throw this.makeError('NETWORK_ERROR');
        }

        // done.answer is the AUTHORITATIVE full text : the caller replaces
        // its accumulated render with buildResult().answerHtml (handoff
        // ordering rule 3 : this heals any client-side accumulation drift).
        return this.buildResult(doneEvent.answer, lastSources, lastRelated, streamSessionId);
      } finally {
        clearTimeout(timeoutId);
      }
    },

    /**
     * Get the current configuration (for debugging).
     * @returns {Object} Current configuration.
     */
    getConfig() {
      return { ...config };
    },

    /**
     * Check if the module is initialized.
     * @returns {boolean} Initialization status.
     */
    isReady() {
      return isInitialized;
    }
  };

  return SmartAssistant;
});
